export function parseSignedAmount(value) {
  if (value === null || value === undefined || value === "") return 0;
  const cleaned = String(value).trim().replace(/^\+/, "");
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}

export function roundMoney(value) {
  return Math.round((parseFloat(value) || 0) * 100) / 100;
}

export function isRoundOffCode(typeCode) {
  const code = (typeCode || "").toUpperCase();
  return code === "ROUNDOFF" || code === "ROUND_OFF";
}

export function getLineItemCategory(typeCode) {
  const code = (typeCode || "").toUpperCase();
  if (code === "GST_ON_HOLD") return "gst_hold";
  if (code === "GST_RECEIVED") return "gst_received";
  return "deduction";
}

export function formatSignedAmount(value) {
  const num = parseFloat(value) || 0;
  if (num > 0) return `+${num.toFixed(2)}`;
  return num.toFixed(2);
}

export function calculateAllocationSummary(allocatedAmount, lineItems, resolveTypeCode) {
  const allocated = roundMoney(allocatedAmount);
  let deductionAppliedTotal = 0;
  let roundOffTotal = 0;
  let gstHoldTotal = 0;
  let gstReceivedTotal = 0;
  const breakdown = [];

  for (const item of lineItems || []) {
    const code = resolveTypeCode
      ? resolveTypeCode(item)
      : item.deduction_type_code || item.code || "";
    const category = getLineItemCategory(code);
    const signed = parseSignedAmount(item.deduction_amount);
    const name =
      item.deduction_type_name ||
      item.name ||
      code ||
      "Line Item";

    if (category === "gst_hold") {
      // Honor the +/- sign entered for GST on hold.
      if (signed !== 0) {
        gstHoldTotal += signed;
        breakdown.push({
          type: name,
          code,
          category,
          amount: signed,
          appliedCredit: signed,
          purpose: "GST on hold (kept in invoice outstanding)",
        });
      }
    } else if (category === "gst_received") {
      gstReceivedTotal += signed;
      if (signed !== 0) {
        breakdown.push({
          type: name,
          code,
          category,
          amount: signed,
          appliedCredit: signed,
          purpose: "GST received with payment",
        });
      }
    } else if (signed !== 0) {
      // Honor the +/- sign entered for TDS, Round Off, and other deductions.
      deductionAppliedTotal += signed;
      if (isRoundOffCode(code)) {
        roundOffTotal += signed;
      }
      breakdown.push({
        type: name,
        code,
        category: isRoundOffCode(code) ? "round_off" : "deduction",
        amount: signed,
        appliedCredit: signed,
        purpose: "Adjusted toward invoice clearance (sign honored)",
      });
    }
  }

  deductionAppliedTotal = roundMoney(deductionAppliedTotal);
  roundOffTotal = roundMoney(roundOffTotal);
  gstHoldTotal = roundMoney(gstHoldTotal);
  gstReceivedTotal = roundMoney(gstReceivedTotal);

  // Amount that reduces the invoice principal balance (GST on hold stays outstanding).
  const principalApplied = roundMoney(allocated + deductionAppliedTotal + gstReceivedTotal);
  // Net applied to invoice includes every line item honoring its sign, incl. GST on hold.
  const netApplied = roundMoney(principalApplied + gstHoldTotal);
  const effectiveReceiptAmount = allocated;
  const settlementAmount = netApplied;

  return {
    allocated,
    deductionAppliedTotal,
    roundOffTotal,
    gstHoldTotal,
    gstReceivedTotal,
    principalApplied,
    netApplied,
    effectiveReceiptAmount,
    settlementAmount,
    breakdown,
  };
}

export function calculateSuggestedAllocateAmount(
  invoiceOutstanding,
  lineItems,
  resolveTypeCode
) {
  const outstanding = roundMoney(invoiceOutstanding);
  const summary = calculateAllocationSummary(0, lineItems, resolveTypeCode);
  return Math.max(
    roundMoney(outstanding - summary.deductionAppliedTotal - summary.gstHoldTotal),
    0
  );
}

export function calculateCashAllocatedTotal(allocations) {
  if (!allocations?.length) return 0;
  return roundMoney(
    allocations.reduce((sum, alloc) => sum + roundMoney(alloc.allocated_amount || 0), 0)
  );
}

export function calculateSettlementTotal(allocations, resolveTypeCode) {
  if (!allocations?.length) return 0;
  return roundMoney(
    allocations.reduce((sum, alloc) => {
      const summary = calculateAllocationSummary(
        alloc.allocated_amount,
        alloc.deductions,
        resolveTypeCode
      );
      return sum + summary.settlementAmount;
    }, 0)
  );
}

export function amountsEqual(a, b, tolerance = 0.001) {
  return Math.abs(roundMoney(a) - roundMoney(b)) <= tolerance;
}

export function matchReceiptAmount(receiptAmount, allocations, resolveTypeCode) {
  const receipt = roundMoney(receiptAmount);
  const cashTotal = calculateCashAllocatedTotal(allocations);
  const settlementTotal = calculateSettlementTotal(allocations, resolveTypeCode);

  if (amountsEqual(receipt, cashTotal)) {
    return { valid: true, mode: "cash", cashTotal, settlementTotal, remaining: 0 };
  }
  if (amountsEqual(receipt, settlementTotal)) {
    return { valid: true, mode: "settlement", cashTotal, settlementTotal, remaining: 0 };
  }

  return {
    valid: false,
    mode: null,
    cashTotal,
    settlementTotal,
    remaining: roundMoney(receipt - cashTotal),
  };
}

export function calculateTotalOutstandingAfterReceipt(invoiceOutstanding, summary) {
  const outstanding = roundMoney(invoiceOutstanding);
  // Only the principal portion reduces the outstanding; GST on hold remains outstanding.
  const invoicePending = Math.max(roundMoney(outstanding - summary.principalApplied), 0);
  const gstOnHold = summary.gstHoldTotal;
  return {
    invoicePending,
    gstOnHold,
    totalPending: invoicePending,
  };
}

/** @deprecated Use calculateCashAllocatedTotal */
export function calculateEffectiveReceiptTotal(allocations) {
  return calculateCashAllocatedTotal(allocations);
}
